import {
  DEFAULT_THEME_SETTINGS,
  LEGACY_PRESET_IDS,
  THEME_PRESETS,
  type ThemeSettings,
  type ThemeTokens,
} from '../../theme/presets'
import { deriveAutoInk, generateAutoInkTheme, generateTheme } from './generatedTheme'
import { toolbarMarkToken, toolbarStatusTokens } from './statusColors'

const LEGACY_PANEL_TERM = String.fromCharCode(98, 105, 110, 100, 101, 114)

function migratePanelTerminology(value: string): string {
  return value.split(LEGACY_PANEL_TERM).join('panel')
}

function migrateThemeOverrides(overrides: ThemeTokens): ThemeTokens {
  return Object.fromEntries(
    Object.entries(overrides).map(([key, value]) => [migratePanelTerminology(key), value]),
  )
}

export function getPreset(presetId: string) {
  return THEME_PRESETS.find(p => p.id === presetId) || THEME_PRESETS[0]
}

export function normalizeThemeSettings(theme?: Partial<ThemeSettings> | null): ThemeSettings {
  const mode = theme?.mode === 'generated' ? 'generated' : 'preset'
  const recipe = ['ink-wash', 'tonal', 'analogous', 'split-complement', 'triadic', 'golden-angle', 'custom'].includes(theme?.recipe || '')
    ? theme!.recipe as ThemeSettings['recipe']
    : DEFAULT_THEME_SETTINGS.recipe
  const contrast = theme?.contrast === 'high' ? 'high' : 'standard'
  const generationBase = theme?.generationBase === 'canvas-only' ? 'canvas-only' : 'canvas-and-ink'
  const customRecipe = typeof theme?.customRecipe === 'string'
    ? migratePanelTerminology(theme.customRecipe)
    : DEFAULT_THEME_SETTINGS.customRecipe
  return {
    ...DEFAULT_THEME_SETTINGS,
    ...(theme || {}),
    mode,
    recipe,
    contrast,
    generationBase,
    customRecipe,
    preset: typeof theme?.preset === 'string'
      ? LEGACY_PRESET_IDS[theme.preset] ?? theme.preset
      : DEFAULT_THEME_SETTINGS.preset,
    overrides: theme?.overrides && typeof theme.overrides === 'object'
      ? migrateThemeOverrides(theme.overrides)
      : {},
  }
}

export function resolveTheme(themeInput: Partial<ThemeSettings>, background: string, foreground: string): ThemeTokens {
  const theme = normalizeThemeSettings(themeInput)
  let tokens: ThemeTokens
  if (theme.mode === 'generated') {
    if (theme.generationBase === 'canvas-only') {
      tokens = generateAutoInkTheme(background, theme.recipe, theme.contrast, theme.overrides['--accent'], theme.customRecipe).tokens
    } else {
      tokens = generateTheme(background, foreground, theme.recipe, theme.contrast, theme.overrides['--accent'], theme.customRecipe).tokens
    }
  } else {
    tokens = { ...getPreset(theme.preset).tokens, ...theme.overrides }
  }
  return { ...tokens, ...toolbarStatusTokens(tokens, theme.contrast), ...toolbarMarkToken(tokens, background) }
}

export function resolveEditorInk(themeInput: Partial<ThemeSettings>, background: string, foreground: string): string {
  const theme = normalizeThemeSettings(themeInput)
  return theme.mode === 'generated' && theme.generationBase === 'canvas-only'
    ? theme.recipe === 'custom'
      ? generateAutoInkTheme(background, 'custom', theme.contrast, undefined, theme.customRecipe).editorInk
      : deriveAutoInk(background, theme.recipe).editorInk
    : foreground
}

export function applyTheme(theme: Partial<ThemeSettings>, background: string, foreground: string) {
  const tokens = resolveTheme(theme, background, foreground)
  const root = document.documentElement
  for (const [key, value] of Object.entries(tokens)) {
    root.style.setProperty(key, value)
  }
}

export function exportTheme(theme: ThemeSettings): string {
  return JSON.stringify({ version: 5, theme: normalizeThemeSettings(theme) }, null, 2)
}

export function importTheme(json: string): ThemeSettings {
  const parsed = JSON.parse(json)
  if ([2, 3, 4, 5].includes(parsed.version) && parsed.theme && typeof parsed.theme === 'object') {
    return normalizeThemeSettings(parsed.theme)
  }
  if (typeof parsed.preset !== 'string' || typeof parsed.overrides !== 'object' || parsed.overrides === null) {
    throw new Error('That does not look like a valid Superelectric Ink theme file.')
  }
  return normalizeThemeSettings(parsed)
}
